import { FriendUser, SocialGraphResponse } from '../types';

let isFetchingGraph = false;

export const SAMPLE_FRIENDS: FriendUser[] = [];

/**
 * Fetch full 2-way social graph including accepted mutual friends,
 * incoming requests, and outgoing sent requests.
 */
export async function fetchSocialGraph(username: string): Promise<SocialGraphResponse> {
  const clean = username?.trim();
  const emptyResponse: SocialGraphResponse = {
    success: true,
    friends: [],
    incomingRequests: [],
    outgoingRequests: [],
    counts: { friends: 0, incoming: 0, outgoing: 0 }
  };

  if (!clean || typeof window === 'undefined') return emptyResponse;

  if (isFetchingGraph) {
    return emptyResponse;
  }
  isFetchingGraph = true;

  try {
    const res = await fetch(`/api/user/friends?username=${encodeURIComponent(clean)}`);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success) {
        return {
          success: true,
          friends: Array.isArray(data.friends) ? data.friends : [],
          incomingRequests: Array.isArray(data.incomingRequests) ? data.incomingRequests : [],
          outgoingRequests: Array.isArray(data.outgoingRequests) ? data.outgoingRequests : [],
          counts: data.counts || {
            friends: data.friends?.length || 0,
            incoming: data.incomingRequests?.length || 0,
            outgoing: data.outgoingRequests?.length || 0
          }
        };
      }
    }
  } catch (err) {
    console.warn('[friendService] fetchSocialGraph notice:', err);
  } finally {
    isFetchingGraph = false;
  }

  return emptyResponse;
}

/**
 * Backward-compatible helper: returns list of accepted mutual friends.
 */
export async function fetchFriends(username: string): Promise<FriendUser[]> {
  const graph = await fetchSocialGraph(username);
  return graph.friends;
}

/**
 * Send a friend request to a target performer.
 * If the target performer already sent caller a request, automatically accepts!
 */
export async function sendFriendRequest(
  username: string,
  friendUsername: string
): Promise<{ success: boolean; message?: string; error?: string; alreadyFriends?: boolean; alreadyPending?: boolean }> {
  const cleanUser = username?.trim();
  const cleanFriend = friendUsername?.trim();

  if (!cleanUser || !cleanFriend) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/friends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: cleanUser,
        friendUsername: cleanFriend,
        action: 'request'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return {
        success: true,
        message: data.message,
        alreadyFriends: data.alreadyFriends,
        alreadyPending: data.alreadyPending
      };
    }
    return { success: false, error: data.error || 'Failed to send friend request' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

// Backward-compatible alias for sendFriendRequest
export const addFriend = sendFriendRequest;

/**
 * Accept an incoming friend request.
 */
export async function acceptFriendRequest(
  username: string,
  friendUsername: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const cleanUser = username?.trim();
  const cleanFriend = friendUsername?.trim();

  if (!cleanUser || !cleanFriend) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/friends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: cleanUser,
        friendUsername: cleanFriend,
        action: 'accept'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to accept friend request' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

/**
 * Decline an incoming friend request.
 */
export async function declineFriendRequest(
  username: string,
  friendUsername: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const cleanUser = username?.trim();
  const cleanFriend = friendUsername?.trim();

  if (!cleanUser || !cleanFriend) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/friends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: cleanUser,
        friendUsername: cleanFriend,
        action: 'decline'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to decline friend request' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

/**
 * Cancel an outgoing friend request sent by caller.
 */
export async function cancelFriendRequest(
  username: string,
  friendUsername: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const cleanUser = username?.trim();
  const cleanFriend = friendUsername?.trim();

  if (!cleanUser || !cleanFriend) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/friends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: cleanUser,
        friendUsername: cleanFriend,
        action: 'cancel'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to cancel friend request' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}

/**
 * Remove an existing mutual friendship.
 */
export async function removeFriend(
  username: string,
  friendUsername: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const cleanUser = username?.trim();
  const cleanFriend = friendUsername?.trim();

  if (!cleanUser || !cleanFriend) {
    return { success: false, error: 'Both usernames are required' };
  }

  try {
    const res = await fetch('/api/user/friends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: cleanUser,
        friendUsername: cleanFriend,
        action: 'remove'
      })
    });

    const data = await res.json() as any;
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    }
    return { success: false, error: data.error || 'Failed to remove friend' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}
